import { Box, IconButton, LinearProgress, Paper, Typography } from '@mui/material';
import { getRuleDescription, isMoreSpecific } from './services/validationRuleService';
import { Badge } from 'framework/ui/mui-builder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { useState } from 'react';
import { RuleValidationResult, TargetRule } from './types';
import EditRuleDialog from './create/EditRuleDialog';
import { PositionType } from 'domain/shared/employee';
import type { Technology } from 'domain/shared/career';

interface TargetRuleLayoutProps {
  rules: TargetRule[];
  onDeleteRule?: (id: string) => void;
  onEditRule?: (id: string, updatedRule: Omit<TargetRule, 'id'>) => void;
  validateRule?: (rule: Omit<TargetRule, 'id'>, editingId?: string) => RuleValidationResult;
  positions: PositionType[];
  technologies: Technology[];
}

const TargetRuleLayout: React.FC<TargetRuleLayoutProps> = ({
  rules,
  onDeleteRule,
  onEditRule,
  validateRule,
  positions,
  technologies,
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<Omit<TargetRule, 'id'>>({ capacity: 0 });
  const editValidation = editingRuleId && validateRule ? validateRule(editingRule, editingRuleId) : { valid: true };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingRuleId(null);
  };

  const handleEditingRuleChange = (field: string, value: any) => {
    const updatedRule = {
      ...editingRule,
      [field]: value,
    };
    setEditingRule(updatedRule);
  };

  const openEditDialog = (rule: TargetRule) => {
    setEditingRuleId(rule.id);
    setEditingRule({
      capacity: rule.capacity,
      competencyLevel: rule.competencyLevel,
      positionId: rule.positionId,
      technologyId: rule.technologyId,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = (updatedRule: Omit<TargetRule, 'id'>) => {
    if (editingRuleId && onEditRule && editValidation.valid && (updatedRule.capacity || 0) > 0) {
      onEditRule(editingRuleId, updatedRule);
      closeEditDialog();
    }
  };
  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h4" color="primary" gutterBottom>
          Target rules
        </Typography>

        {rules.length === 0 ? (
          <Typography color="textSecondary">Target rules are not defined yet. Create your first rule above.</Typography>
        ) : (
          <>
            {rules.map(rule => {
              const nestedRules = rules.filter(r => r.id !== rule.id && isMoreSpecific(r, rule));

              const nestedCount = nestedRules.reduce((sum, r) => sum + r.capacity, 0);
              const availableCount = Math.max(0, rule.capacity - nestedCount);
              const nestedPercentage = rule.capacity > 0 ? (nestedCount / rule.capacity) * 100 : 0;

              const position = positions.find((element: PositionType) => element.id === rule.positionId);
              const technology = technologies.find((element: Technology) => element.id === rule.technologyId);

              return (
                <Box
                  data-testid="target-rule-row"
                  key={rule.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'border.default',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography data-testid="capacity">
                      <strong>{rule.capacity}</strong> {getRuleDescription(rule.competencyLevel, position, technology)}
                      {nestedCount > 0 && (
                        <Typography component="span" variant="body1" color="textSecondary">
                          {` (${availableCount} available, ${nestedCount} in nested rules)`}
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {rule.competencyLevel && rule.competencyLevel !== '' && (
                        <Badge data-testid="competency-level" sx={{ mr: 0.5 }}>
                          {rule.competencyLevel}
                        </Badge>
                      )}
                      {position && (
                        <Badge data-testid="position" color="primary" sx={{ mr: 0.5 }}>
                          {position.name}
                        </Badge>
                      )}
                      {technology && (
                        <Badge data-testid="technology" color="secondary" sx={{ mr: 0.5 }}>
                          {technology.name}
                        </Badge>
                      )}
                      {onEditRule && (
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={`Edit rule ${rule.id}`}
                          onClick={() => openEditDialog(rule)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {onDeleteRule && (
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Delete rule ${rule.id}`}
                          onClick={() => onDeleteRule(rule.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {nestedCount > 0 && (
                    <Box sx={{ mt: 1, width: '100%' }}>
                      <LinearProgress
                        variant="determinate"
                        value={nestedPercentage}
                        color="primary"
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                      <Typography data-testid="affected-rule" variant="body2" color="textSecondary" mt={0.5}>
                        {nestedPercentage.toFixed(0)}% allocated to more specific rules
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      <EditRuleDialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        rule={editingRule}
        onSave={handleSaveEdit}
        validation={editValidation}
        onChange={handleEditingRuleChange}
        positions={positions}
        technologies={technologies}
      />
    </>
  );
};
export default TargetRuleLayout;
